/**
 * Utility functions for lab value range checking and color coding
 */

/**
 * Parse reference range string into low and high values
 * Supports formats like: "70-100", "< 5.7", "> 40", "0-0.4", etc.
 */
export const parseReferenceRange = (rangeString) => {
  if (!rangeString || typeof rangeString !== 'string') {
    return null;
  }

  const range = rangeString.trim();
  
  // Handle "< value" format
  if (range.startsWith('<')) {
    const value = parseFloat(range.substring(1).trim());
    if (!isNaN(value)) {
      return { low: null, high: value, type: 'upper_limit' };
    }
  }
  
  // Handle "> value" format
  if (range.startsWith('>')) {
    const value = parseFloat(range.substring(1).trim());
    if (!isNaN(value)) {
      return { low: value, high: null, type: 'lower_limit' };
    }
  }
  
  // Handle "low-high" format
  if (range.includes('-')) {
    const parts = range.split('-');
    if (parts.length === 2) {
      const low = parseFloat(parts[0].trim());
      const high = parseFloat(parts[1].trim());
      if (!isNaN(low) && !isNaN(high)) {
        return { low, high, type: 'range' };
      }
    }
  }
  
  // Handle single value (treat as exact target)
  const singleValue = parseFloat(range);
  if (!isNaN(singleValue)) {
    return { low: singleValue, high: singleValue, type: 'exact' };
  }
  
  return null;
};

/**
 * Determine if a lab value is within normal range
 * Returns: 'normal', 'high', 'low', 'critical-high', 'critical-low', or 'unknown'
 */
export const getLabValueStatus = (value, referenceRange) => {
  if (value === null || value === undefined || value === '' || value === '-') {
    return 'unknown';
  }
  
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return 'unknown';
  }
  
  const parsedRange = parseReferenceRange(referenceRange);
  if (!parsedRange) {
    return 'unknown';
  }
  
  const { low, high, type } = parsedRange;
  
  switch (type) {
    case 'upper_limit':
      if (numericValue <= high) return 'normal';
      if (numericValue > high * 2) return 'critical-high'; // Critical if > 2x upper limit
      return 'high';
      
    case 'lower_limit':
      if (numericValue >= low) return 'normal';
      if (numericValue < low * 0.5) return 'critical-low'; // Critical if < 50% of lower limit
      return 'low';
      
    case 'range':
      if (numericValue >= low && numericValue <= high) return 'normal';
      
      // Calculate how far outside the range
      const rangeWidth = high - low;
      const criticalThreshold = rangeWidth * 0.5; // 50% beyond range is critical
      
      if (numericValue > high) {
        if (numericValue > high + criticalThreshold) return 'critical-high';
        return 'high';
      } else {
        if (numericValue < low - criticalThreshold) return 'critical-low';
        return 'low';
      }
      
    case 'exact':
      return numericValue === low ? 'normal' : 'unknown';
      
    default:
      return 'unknown';
  }
};

/**
 * Get color and style for lab value based on status
 */
export const getLabValueStyle = (status) => {
  switch (status) {
    case 'critical-high':
    case 'critical-low':
      return {
        backgroundColor: '#fff1f0',
        color: '#f5222d',
        fontWeight: 'bold',
        padding: '2px 4px',
        borderRadius: '2px',
        display: 'inline-block'
      };
      
    case 'high':
      return {
        backgroundColor: '#fffbe6',
        color: '#faad14',
        fontWeight: 'bold',
        padding: '2px 4px',
        borderRadius: '2px',
        display: 'inline-block'
      };
      
    case 'low':
      return {
        backgroundColor: '#e6f7ff',
        color: '#1890ff',
        fontWeight: 'bold',
        padding: '2px 4px',
        borderRadius: '2px',
        display: 'inline-block'
      };
      
    case 'normal':
      return {
        color: '#000000',
        fontWeight: 'normal'
      };
      
    default:
      return {};
  }
};

/**
 * Get status text for display
 */
export const getLabStatusText = (status) => {
  switch (status) {
    case 'critical-high': return 'Critical High';
    case 'critical-low': return 'Critical Low';
    case 'high': return 'High';
    case 'low': return 'Low';
    case 'normal': return 'Normal';
    default: return '';
  }
};

/**
 * Determine lab value status with fallback to existing flag
 * This function prioritizes explicit flags but falls back to range-based calculation
 */
export const getLabValueStatusWithFlag = (value, referenceRange, existingFlag) => {
  // If there's an existing flag, use it
  if (existingFlag) {
    if (existingFlag === 'H' || existingFlag === 'HIGH') return 'high';
    if (existingFlag === 'L' || existingFlag === 'LOW') return 'low';
    if (existingFlag === 'A') return 'high'; // Abnormal - treat as high
    if (existingFlag === 'C') return 'critical-high'; // Critical
  }
  
  // Otherwise, calculate based on reference range
  return getLabValueStatus(value, referenceRange);
};
